import { useCallback, useRef, type MutableRefObject } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { type GridApi, type GridInitialState } from "@mui/x-data-grid-pro";

interface ListState {
  grid: GridInitialState;
  scrollY: number;
}

// 履歴エントリ単位で保持する。APIの実データ・認証情報は保存しない。
// 再読み込み/別タブでは空になるので、詳細の直リンクは安全に一覧へフォールバックする。
const listStates = new Map<string, ListState>();
export const hasAllMattersListState = (key: unknown): key is string =>
  typeof key === "string" && listStates.has(key);

export const useAllMattersListState = (
  apiRef: MutableRefObject<GridApi>
): {
  initialState: GridInitialState | undefined;
  saveListState: () => void;
  restoreScroll: () => (() => void) | undefined;
} => {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const restored = useRef(false);
  const snapshot = navigationType === "POP" ? listStates.get(key) : undefined;

  const saveListState = useCallback(() => {
    listStates.delete(key);
    listStates.set(key, {
      grid: apiRef.current.exportState(),
      scrollY: window.scrollY,
    });
    // 長時間利用でも履歴の保持件数を制限する。
    const oldestKey = Array.from(listStates.keys())[0];
    if (listStates.size > 20 && oldestKey !== undefined)
      listStates.delete(oldestKey);
  }, [key, apiRef]);

  const restoreScroll = useCallback(() => {
    if (snapshot === undefined || restored.current) return;
    const frame = requestAnimationFrame(() => {
      window.scrollTo(0, snapshot.scrollY);
      restored.current = true;
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [snapshot]);

  return { initialState: snapshot?.grid, saveListState, restoreScroll };
};
